import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  PLATFORM_ID,
} from '@angular/core';
import { I18nService } from '../../services/i18n.service';

/**
 * Scales host font-size so translated overlay text keeps the same left/right
 * footprint (one line, equal side inset) across en/ar/ml — e.g. "My Bid" /
 * "Lost Your Bid" and their translations.
 */
@Directive({
  selector: '[logibidsFitText]',
})
export class FitTextDirective {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(I18nService);

  /** Equal left/right gap from the overlay edge. */
  readonly sideInset = input(12, { alias: 'fitInset' });
  readonly minPx = input(16, { alias: 'fitMin' });
  readonly maxPx = input(48, { alias: 'fitMax' });

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private rafId = 0;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    afterNextRender(() => {
      this.setupObservers();
      this.scheduleFit();
    });

    effect(() => {
      this.i18n.language();
      this.i18n.isReady();
      this.scheduleFit();
    });

    this.destroyRef.onDestroy(() => {
      cancelAnimationFrame(this.rafId);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
    });
  }

  private setupObservers(): void {
    const host = this.el.nativeElement;
    host.style.whiteSpace = 'nowrap';
    host.style.display = 'inline-block';
    host.style.verticalAlign = 'middle';
    host.style.textAlign = 'center';
    host.style.boxSizing = 'border-box';
    host.style.lineHeight = '1.1';
    host.style.margin = '0';
    host.style.maxWidth = '100%';

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
      const parent = host.parentElement;
      if (parent) {
        this.resizeObserver.observe(parent);
      }

      this.mutationObserver = new MutationObserver(() => this.scheduleFit());
      this.mutationObserver.observe(host, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    });
  }

  private scheduleFit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => this.fit());
  }

  /**
   * Finds the largest font size whose intrinsic text width fits inside
   * parentWidth − 2×sideInset — same L/R spacing for every language.
   */
  private fit(): void {
    const host = this.el.nativeElement;
    const parent = host.parentElement;
    if (!parent || !host.textContent?.trim()) {
      return;
    }

    const inset = this.sideInset();
    const available = parent.clientWidth - inset * 2;
    if (available <= 0) {
      return;
    }

    // Measure intrinsic text width (not the stretched 100% box width)
    host.style.width = 'max-content';
    host.style.maxWidth = 'none';

    const min = this.minPx();
    const max = this.maxPx();
    let low = min;
    let high = max;
    let best = min;

    while (high - low > 0.5) {
      const mid = (low + high) / 2;
      host.style.fontSize = `${mid}px`;
      if (host.scrollWidth <= available) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    host.style.fontSize = `${best}px`;
    host.style.width = 'max-content';
    host.style.maxWidth = `${available}px`;
  }
}
